import { config } from '../../../config/runtimeConfig';
import { getUrlParam } from '../../../utils/urlParams';

/**
 * Builds the URL the print QR encodes. Two scenarios (issue #878 / meeting
 * #4662, extended in issue #897):
 *
 *  1. `dealerCatalogUrl` (or `dealerSiteUrl` fallback) configured AND car
 *     is used-VIN →
 *     `${dealerSiteUrl}/?widget-view=mini-car&carId=<id>&token=<token>#/cars/<id>`.
 *     After scanning, the user lands on the dealer's own site, where the
 *     embedded widget catches the params and opens a mini-car modal over
 *     the host page. The hash `#/cars/<id>` deeplinks the widget catalog
 *     HashRouter to CarDetailPage, so when the user closes the modal the
 *     specific car detail page is rendered underneath (issue #897).
 *
 *     `dealerCatalogUrl` (catalog page URL) is preferred over `dealerSiteUrl`
 *     (origin only) — embed-script on host page mounts widget in `full` mode
 *     only on catalog page, on root it's `chat-only` and our overlay is
 *     missing.
 *
 *  2. Otherwise (legacy / new-model showroom flow / dealerSiteUrl absent) →
 *     `${window.location.origin}/#/cars/:id/landing?token=...` — standalone
 *     landing on demo.shiftgears.ai.
 *
 * For new-model cars (Autodis OMODA/JAECOO) the host site is not yet
 * widget-integrated, so we always fall through to scenario 2 even when
 * `dealerSiteUrl` is set.
 *
 * @param {object} car — Car object (DealerCarDto).
 * @returns {string} fully-qualified URL safe to embed in a QR code.
 */
export const buildLandingUrl = (car, mode = 'full') => {
  const fallbackToken = config.FRONTEND_TOKEN;
  const fallbackOrg = config.ORGANIZATION_ID;
  const fallbackSlug = config.INVENTORY_SLUG;
  const dealerCatalogUrl = config.DEALER_CATALOG_URL;
  const dealerSiteUrl = dealerCatalogUrl || config.DEALER_SITE_URL;
  const landingToken = getUrlParam('token') || fallbackToken;

  // Catalog QR (#1022 — Маша, общий salon QR-код). Кодирует salon Welcome
  // на самом demo-сайте: `${window.location.origin}/#/salon?token=...`.
  //
  // НЕ используем `${dealerSiteUrl}/?widget-view=catalog` (#1060): во-первых
  // overlay-handler `widget-view=catalog` на сайте дилера ещё не реализован;
  // во-вторых `dealerSiteUrl` в standalone резолвится из конфига токена
  // (primaryOrigin → allowedOrigins[0]), а у Autodis allowedOrigins[0] =
  // `http://localhost:5173` → QR кодировал localhost даже на test/prod.
  // Целевой URL — деплой-демо `/#/salon` (origin печатной страницы =
  // demo-домен, т.к. b2b-ui открывает catalog-qr на demoSiteUrl).
  if (mode === 'catalog-qr') {
    const qs = new URLSearchParams();
    if (landingToken) {
      qs.set('token', landingToken);
    } else {
      if (fallbackOrg) qs.set('organizationId', String(fallbackOrg));
      if (fallbackSlug) qs.set('inventorySlug', fallbackSlug);
    }
    return `${window.location.origin}/#/salon?${qs.toString()}`;
  }

  if (!car?.isNewModel && dealerSiteUrl) {
    const qs = new URLSearchParams();
    qs.set('widget-view', 'mini-car');
    qs.set('carId', String(car?.id ?? ''));
    if (landingToken) qs.set('token', landingToken);
    const base = dealerSiteUrl.replace(/\/+$/, '');
    // Hash `#/cars/<id>` — deeplink to the widget catalog HashRouter's
    // CarDetailPage. After the user closes the mini-car modal, the
    // specific car page is rendered underneath, not the catalog list.
    return `${base}/?${qs.toString()}#/cars/${car?.id ?? ''}`;
  }

  const landingQs = new URLSearchParams();
  if (landingToken) {
    landingQs.set('token', landingToken);
  } else {
    if (fallbackOrg) landingQs.set('organizationId', String(fallbackOrg));
    if (fallbackSlug) landingQs.set('inventorySlug', fallbackSlug);
  }
  return `${window.location.origin}/#/cars/${car?.id}/landing?${landingQs.toString()}`;
};
